/*
 * QuoteForm.tsx : Un formulaire pour ajouter ou modifier un devis.
 */

// Import React
import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';

// Import FontAwesome
import { faFileContract } from '@fortawesome/free-solid-svg-icons';

// Import Communs et Layout
import {
	AlertDialog,
	CheckboxField,
	finishFormSave,
	FormSaveActions,
	InputField,
	SelectField,
	useFormDirty,
} from '../../../common';
import CardHeader from 'layout/CardHeader';

// Import Tiers pour le select
import { tiersApi } from 'features/tiers';

// Import Devis
import { QuoteData } from '../types/types';
import { quotesApi } from '../services/quotesApi';
import { quotesClassificationApi } from '../services/quoteClassificationApi';

// Composant QuoteForm
const QuoteForm: React.FC = () => {
	// Hook de Chargement
	const [loading, setLoading] = React.useState(true);
	const [baselineReady, setBaselineReady] = React.useState(false);

	// Hook de Formulaire
	const [formData, setFormData] = React.useState<QuoteData>({
		id: 0,
		referenceNumber: '',
		budgeted: false,
		quoteVersions: [],
	});

	// Hook pour tracer si l'utilisateur a fait un choix explicite sur budgeted
	const [budgetedChosen, setBudgetedChosen] = React.useState(false);

	// Hook d'erreur pour la validation
	const [validationError, setValidationError] = React.useState<string | null>(
		null
	);
	const [showErrorDialog, setShowErrorDialog] = React.useState(false);

	// Hook de Navigation et Paramètres de navigation
	const navigate = useNavigate();
	const { id } = useParams<{ id: string }>();
	const { isDirty, markClean } = useFormDirty(formData, baselineReady);

	// Hooks pour le select des tiers
	const [TiersOptions, setTiersOptions] = React.useState<
		{ key: string; label: string }[]
	>([]);
	const [loadingTiers, setLoadingTiers] = React.useState(true);

	// Hooks pour le select des classifications de devis
	const [ClassificationsOptions, setClassificationsOptions] = React.useState<
		{ id: number; key: string; label: string }[]
	>([]);
	const [loadingClassifications, setLoadingClassifications] =
		React.useState(true);

	// Hook pour le prochain numéro de référence (uniquement AACCC ou A = année et C = compteur)
	const [nextRef, setNextRef] = React.useState<string | null>(null);
	const [nextRefFormation, setNextRefFormation] = React.useState<string | null>(
		null
	);

	// Loading : Fetch des données et remplissage du formulaire (une fois le composant monté, si un id est présent)
	React.useEffect(() => {
		// Vérification du token d'authentification
		const token = localStorage.getItem('token');
		if (!token) return;

		// Si un id est présent, on fetch les données
		if (id) {
			quotesApi
				.get(Number(id), token)
				.then((data: QuoteData) => {
					setFormData(data);
					setBudgetedChosen(true);
					// Préremplir les selects
					if (data.tiers) {
						setTiersOptions([
							{
								key: data.tiers.id.toString(),
								label: data.tiers.label,
							},
						]);
					}
					if (data.classification) {
						setClassificationsOptions([
							{
								id: data.classification.id,
								key: data.classification.code,
								label: data.classification.label,
							},
						]);
					}
				})
				.catch((error) => {
					console.error('Erreur lors du chargement du devis :', error);
					setLoading(false);
				})
				.finally(() => {
					setLoading(false);
					setBaselineReady(true);
				});
		} else {
			// Sinon, pas d'id, on arrête le loading
			setLoading(false);
		}
	}, [id]);

	// Lazy load des options de tiers
	React.useEffect(() => {
		const token = localStorage.getItem('token');
		if (!token) return;

		if (!loading && loadingTiers) {
			tiersApi
				.listSelectClients(token)
				.then((items) => {
					const options = items.map((item) => ({
						key: item.id.toString(),
						label: String(item.label),
					}));
					setTiersOptions(options);
				})
				.catch((error) => console.error(error))
				.finally(() => setLoadingTiers(false));
		}
	}, [loading, loadingTiers]);

	// Lazy load des options de classifications de devis
	React.useEffect(() => {
		const token = localStorage.getItem('token');
		if (!token) return;

		if (!loading && loadingClassifications) {
			quotesClassificationApi
				.listPaginated(token, { limit: 999999, sort: 'label', order: 'ASC' })
				.then((res) => {
					setClassificationsOptions(
						res.data
							.filter((classification) => classification.parent !== null)
							.map((classification) => ({
								id: classification.id,
								key: classification.code,
								label: classification.label,
							}))
					);
				})
				.catch((error) => console.error(error))
				.finally(() => setLoadingClassifications(false));
		}
	}, [loading, loadingClassifications]);

	// Lazy load du prochain numéro de référence
	React.useEffect(() => {
		// Vérification de la création d'une nouvelle commande (sinon on ne garde que les chiffres de la référence existante)
		if (id) {
			setNextRef(formData?.referenceNumber?.replace(/\D/g, '') || null);
			return;
		}

		// Vérification du token d'authentification
		const token = localStorage.getItem('token');
		if (!token) return;

		// Si on n'a pas déjà récupéré la prochaine référence
		if (!loading && nextRef === null) {
			// Récupération de l'année en cours
			const year = new Date().getFullYear();

			// Fetch du prochain numéro de référence
			quotesApi
				.previewReference(token, year)
				.then((data) => {
					setNextRef(data.nextReference);
					let didWrite = false;
					let waitingForFormation = false;
					setFormData((prevData) => {
						const code = prevData.classification?.code;
						if (code && code !== 'F') {
							didWrite = true;
							return {
								...prevData,
								referenceNumber: 'M' + data.nextReference + code,
							};
						}
						if (code === 'F') {
							waitingForFormation = true;
						}
						return prevData;
					});
					if (didWrite || !waitingForFormation) {
						setBaselineReady(true);
					}
				})
				.catch((error) => {
					console.error(error);
					setBaselineReady(true);
				});
		}
	}, [loading]);

	// Lazy load du prochain numéro de référence de formation
	React.useEffect(() => {
		// Vérification de la création d'une nouvelle commande (sinon on ne garde que les chiffres de la référence existante)
		if (id) {
			setNextRefFormation(
				formData?.referenceNumber?.replace(/\D/g, '') || null
			);
			return;
		}

		// Vérification du token d'authentification
		const token = localStorage.getItem('token');
		if (!token) return;

		// Si on n'a pas déjà récupéré la prochaine référence
		if (!loading && nextRefFormation === null) {
			// Récupération de l'année en cours
			const year = new Date().getFullYear();

			// Fetch du prochain numéro de référence
			quotesApi
				.previewReferenceFormation(token, year)
				.then((data) => {
					setNextRefFormation(data.nextReference);
					let didWrite = false;
					setFormData((prevData) => {
						if (prevData.classification?.code === 'F') {
							didWrite = true;
							return {
								...prevData,
								referenceNumber: 'F' + data.nextReference,
							};
						}
						return prevData;
					});
					if (didWrite) {
						setBaselineReady(true);
					}
				})
				.catch((error) => {
					console.error(error);
					setBaselineReady(true);
				});
		}
	}, [loading]);

	// Handle Formulaire : Soumission
	const [isSubmitting, setIsSubmitting] = React.useState(false);

	const navigateAfterEditSuccess = () => {
		// On recherche la dernière version du devis correspondant et on navigue vers sa page de gestion
		const latestVersion = formData.quoteVersions
			?.filter((v) => v.status !== 'CANCELED')
			?.reduce((prev, current) =>
				prev.versionIndex > current.versionIndex ? prev : current
			);
		if (latestVersion) {
			navigate(`/app/quoteVersions/${latestVersion.id}/manage`);
		} else {
			console.warn("Aucune version de devis trouvée pour l'élément", id);
		}
	};

	const handleSave = (shouldLeave: boolean) => {
		const errors: string[] = [];

		if (!formData.classification || !formData.classification.code) {
			errors.push('La classification du devis est obligatoire.');
		}

		if (!formData.referenceNumber || formData.referenceNumber.trim() === '') {
			errors.push('Le numéro de référence est obligatoire.');
		}

		if (!formData.tiers || !formData.tiers.id) {
			errors.push('Le tiers est obligatoire.');
		}

		if (!budgetedChosen) {
			errors.push(
				'Vous devez faire un choix pour le champ Budgétisé (Oui ou Non).'
			);
		}

		if (errors.length > 0) {
			setValidationError(errors.join('\n'));
			setShowErrorDialog(true);
			return;
		}

		// Vérifier la présence du token d'authentification
		const token = localStorage.getItem('token');
		if (!token) return;
		// Indiquer que la soumission est en cours
		setIsSubmitting(true);
		// Si un id est présent, mise à jour du devis, puis on navigue vers la page de la dernière version du devis
		if (id) {
			quotesApi
				.update(Number(id), formData, token)
				.then(() =>
					finishFormSave(shouldLeave, {
						onLeave: navigateAfterEditSuccess,
						markClean,
					})
				)
				.catch((error) =>
					console.error('Erreur lors de la mise à jour du devis :', error)
				)
				.finally(() => setIsSubmitting(false));
		} else {
			// Sinon, création d'un nouveau devis, et on supprime le numéro de référence car il doit être recalculé côté backend pour éviter les conflits
			const keysToRemove = ['referenceNumber'] as const;

			const dataToCreate = Object.fromEntries(
				Object.entries(formData).filter(
					([key]) =>
						!keysToRemove.includes(key as (typeof keysToRemove)[number])
				)
			);

			quotesApi
				.create(dataToCreate, token)
				.then((created) =>
					finishFormSave(shouldLeave, {
						onLeave: () => navigate('/app/quotes'),
						markClean,
						stayOnEdit: created?.id
							? () =>
									navigate(`/app/quotes/${created.id}/edit`, {
										replace: true,
									})
							: undefined,
					})
				)
				.catch((error) =>
					console.error('Erreur lors de la création du devis :', error)
				)
				.finally(() => setIsSubmitting(false));
		}
	};

	const handleSubmit = (e: React.FormEvent) => {
		// Empêcher le comportement par défaut du formulaire
		e.preventDefault();
		handleSave(true);
	};

	// Handle Formulaire : Changements des inputs
	const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const { name, value } = e.target;
		setFormData({ ...formData, [name]: value });
	};

	// Handle Formulaire : Changements des selects
	const handleChangeSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
		const { name, value } = e.target;
		// Si name est tiers, on doit mettre à jour l'objet complet
		if (name === 'tiers') {
			const selectedOption = TiersOptions.find((opt) => opt.key === value);
			if (selectedOption) {
				setFormData({
					...formData,
					tiers: {
						id: selectedOption.key as unknown as number,
						label: selectedOption.label,
					},
				});
			}
			return;
		}
		// Si name est classification, on doit mettre à jour l'objet complet
		if (name === 'classification') {
			const selectedOption = ClassificationsOptions.find(
				(opt) => opt.key === value
			);
			if (selectedOption) {
				setFormData({
					...formData,
					classification: {
						id: selectedOption.id,
						code: selectedOption.key,
						label: selectedOption.label,
					},
					referenceNumber:
						selectedOption.key === 'F'
							? 'F' + nextRefFormation
							: 'M' + nextRef + selectedOption.key,
				});
			}
			return;
		}

		// Pour les autres champs, mise à jour standard
		setFormData({ ...formData, [name]: value });
	};

	// Rendu du composant principal
	return (
		<div>
			{/* DIALOG D'ERREUR DE VALIDATION */}
			<AlertDialog
				label="Erreurs de validation"
				text={
					validationError
						? validationError
						: 'Une erreur est survenue lors de la validation du formulaire.'
				}
				open={showErrorDialog}
				setOpen={setShowErrorDialog}
			/>

			{/* CARD PRINCIPALE */}
			<div className="max-w-3xl mx-auto p-6 bg-white rounded-lg shadow-md">
				{/* HEADER DE LA CARD */}
				<CardHeader
					icon={faFileContract}
					title={id ? "Édition d'un devis" : 'Nouveau devis'}
					createdAt={
						formData.createdAt
							? formData.createdAt?.toLocaleString()
							: undefined
					}
					createdBy={
						formData.creator
							? formData.creator.firstName + ' ' + formData.creator.name
							: undefined
					}
					loading={loading}
				/>

				{/* CONTENU DE LA CARD */}
				<div className="items-center space-x-4 mt-3 w-full">
					<form onSubmit={handleSubmit} className="space-y-4">
						{/* 2 COLONNES */}
						<div className="grid grid-cols-1 md:grid-cols-2 gap-16">
							{/* COLONNE GAUCHE */}
							<div className="space-y-4">
								<SelectField
									label="Classification du devis"
									selectedKey={formData.classification?.code || ''}
									onValueChange={(val) =>
										handleChangeSelect({
											target: {
												name: 'classification',
												value: val,
											},
										} as React.ChangeEvent<HTMLSelectElement>)
									}
									options={ClassificationsOptions}
									loading={loading}
									optionsLoading={loadingClassifications}
								/>
								<InputField
									label="Numéro de référence"
									name="referenceNumber"
									value={formData.referenceNumber}
									onChange={handleChange}
									required
									loading={loading}
									placeholder="Calculé selon la classification"
									readOnly={id ? false : true}
								/>
							</div>

							{/* COLONNE DROITE */}
							<div className="space-y-4">
								<SelectField
									label="Tiers"
									selectedKey={formData.tiers?.id.toString() || ''}
									onValueChange={(val) =>
										handleChangeSelect({
											target: {
												name: 'tiers',
												value: val,
											},
										} as React.ChangeEvent<HTMLSelectElement>)
									}
									options={TiersOptions}
									loading={loading}
									optionsLoading={loadingTiers}
									entityHref={
										formData.tiers?.id
											? `/app/tiers/${formData.tiers.id}/edit`
											: undefined
									}
								/>
								<div className="space-y-3">
									<label className="block text-sm font-medium text-gray-700">
										Budgétisé
									</label>
									<div className="space-y-2 pl-2">
										<CheckboxField
											label="Oui"
											checked={formData.budgeted === true}
											onChange={(checked) => {
												if (checked) {
													setFormData({ ...formData, budgeted: true });
													setBudgetedChosen(true);
												}
											}}
											loading={loading}
										/>
										<CheckboxField
											label="Non"
											checked={formData.budgeted === false && budgetedChosen}
											onChange={(checked) => {
												if (checked) {
													setFormData({ ...formData, budgeted: false });
													setBudgetedChosen(true);
												}
											}}
											loading={loading}
										/>
									</div>
								</div>
							</div>
						</div>
					</form>

					<FormSaveActions
						isDirty={isDirty}
						isSubmitting={isSubmitting}
						onCancel={() => {
							if (id) {
								navigateAfterEditSuccess();
							} else {
								navigate('/app/quotes');
							}
						}}
						onSave={() => handleSave(false)}
						onSaveAndLeave={() => handleSave(true)}
					/>
				</div>
			</div>
		</div>
	);
};

export default QuoteForm;
